import type { AGUAppLocation } from 'agu_app_shell';
import type { OEPNavItem, OEPView } from 'agu_oep_core';

/** The OEP surface's single module key — the `m=` URL param (LOCATION_PARAM_KEYS). */
export const OEP_MODULE_KEY = 'oep';

/** An in-surface OEP place: the shell's location triple, narrowed to OEP's views. */
export type OEPLocation = AGUAppLocation<OEPView> & { readonly ModuleKey: typeof OEP_MODULE_KEY };

const VIEWS: readonly OEPView[] = ['home', 'presentation', 'reviews', 'review', 'nominate', 'nomination', 'committee', 'program-status', 'activity', 'user-management', 'resources'];
/** Views parameterized by a record (review = ReviewAssignment ID, nomination = Presentation ID). */
const WITH_RECORD: ReadonlySet<OEPView> = new Set<OEPView>(['review', 'nomination']);
/** Views that keep an optional record (committee = the chosen Section ID, for a member of two sections' committees). */
const OPTIONAL_RECORD: ReadonlySet<OEPView> = new Set<OEPView>(['committee']);

function IsOEPView(view: string): view is OEPView {
    return (VIEWS as readonly string[]).includes(view);
}

/** Build an OEP location; the record ID is kept only on views that take one. */
export function OEPAt(view: OEPView, recordID?: string): OEPLocation {
    return (WITH_RECORD.has(view) || OPTIONAL_RECORD.has(view)) && recordID
        ? { ModuleKey: OEP_MODULE_KEY, View: view, RecordID: recordID }
        : { ModuleKey: OEP_MODULE_KEY, View: view };
}

/**
 * Narrow any shell location (typically a deep link) to an OEP location.
 * Fails closed: a foreign module, an unknown view, or a record view without a record is null.
 */
export function ParseOEPLocation(loc: AGUAppLocation | null): OEPLocation | null {
    if (!loc || loc.ModuleKey !== OEP_MODULE_KEY || !IsOEPView(loc.View)) return null;
    if (WITH_RECORD.has(loc.View) && !loc.RecordID) return null;
    return OEPAt(loc.View, loc.RecordID);
}

/** Views every OEP user may open without a nav entry: Home, Resources, and the record views a task links to. */
const ALWAYS_ALLOWED: ReadonlySet<OEPView> = new Set<OEPView>(['home', 'resources', 'review', 'nomination']);

/**
 * The one allow-list for every way a user can arrive at a place — deep link, browser Back/Forward,
 * a hand-edited URL: an always-allowed view or one in the role-derived nav. Client-side only; the
 * server (RLS, action guards) is the real boundary.
 */
export function IsOEPLocationAllowed(loc: OEPLocation, nav: readonly OEPNavItem[]): boolean {
    return ALWAYS_ALLOWED.has(loc.View) || nav.some((n) => n.View === loc.View);
}

/** Where a requested place actually lands: the place when allowed, otherwise Home. */
export function OEPLandingFor(loc: OEPLocation | null, nav: readonly OEPNavItem[]): OEPLocation {
    return loc !== null && IsOEPLocationAllowed(loc, nav) ? loc : OEPAt('home');
}
